/**
 * IntegrationsHealth — admin-only page at /admin/integrations.
 *
 * Surfaces a colour-coded table of every 3rd-party integration we use:
 * Stripe, Resend, Facebook, Meta CAPI, LinkedIn, Twilio, OpenAI,
 * Flinks, Turnstile, GA4, Slack. Two views: surface check (config-only)
 * and probe (live network round-trip). Token-gated.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Activity, Loader2 } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const TOKEN_KEY = "crc_admin_token";

function StatusPill({ status }) {
  const cfg = {
    green: { Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Live" },
    amber: { Icon: AlertTriangle, cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Partial" },
    red:   { Icon: XCircle, cls: "bg-rose-100 text-rose-800 border-rose-200", label: "Missing" },
  }[status] || { Icon: XCircle, cls: "bg-slate-100 text-slate-700 border-slate-200", label: status };
  const { Icon, cls, label } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${cls}`}>
      <Icon className="w-3 h-3"/> {label}
    </span>
  );
}

const NICE = {
  stripe: { label: "Stripe", purpose: "Rent payments + landlord subscriptions" },
  resend: { label: "Resend", purpose: "Transactional + marketing email" },
  facebook_page: { label: "Facebook Page", purpose: "Auto-posted listings + content engine" },
  meta_capi: { label: "Meta CAPI", purpose: "Server-side ad conversions" },
  linkedin: { label: "LinkedIn", purpose: "Auto-post + lead-gen webhook" },
  twilio_sms: { label: "Twilio SMS", purpose: "Engagement nudges + hot-lead pings" },
  emergent_llm: { label: "AI Engine (Claude/Gemini/GPT)", purpose: "Every Smart feature" },
  flinks: { label: "Flinks (Canadian bank linking)", purpose: "Tenant + landlord bank accounts" },
  turnstile: { label: "Cloudflare Turnstile", purpose: "Bot defense (invisible captcha)" },
  ga4_mp: { label: "GA4 Measurement Protocol", purpose: "Server-side analytics" },
  slack: { label: "Slack (hot-lead webhook)", purpose: "#sales hot-lead push" },
};

export default function IntegrationsHealth() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [probing, setProbing] = useState(false);

  const load = async (probe = false) => {
    if (!token) return;
    if (probe) setProbing(true); else setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/integrations/status?token=${encodeURIComponent(token)}${probe ? "&probe=true" : ""}`);
      if (!r.ok) {
        if (r.status === 401) toast.error("Bad token — paste your ADMIN_CONFIG_TOKEN.");
        else toast.error(`Health check failed (${r.status})`);
        return;
      }
      const d = await r.json();
      setData(d);
      localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      toast.error("Network error");
    } finally {
      setLoading(false);
      setProbing(false);
    }
  };

  useEffect(() => {
    if (token) load(false);
  }, []);  // intentionally one-shot on mount

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader light/>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 text-emerald-700">
          <Activity className="w-4 h-4"/>
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold">Admin · Integration health</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 mt-2 leading-tight tracking-tight">
          Are all the keys working?
        </h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          One panel for every 3rd-party we depend on. Surface check is instant. Probe pings each provider live and reports back in under 10 s.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <input
            type="password"
            placeholder="Admin token (ADMIN_CONFIG_TOKEN)"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="ih-token"
            className="flex-1 px-3 py-2 rounded-lg border border-slate-300 focus:border-emerald-500 outline-none text-sm"
          />
          <button
            onClick={() => load(false)}
            disabled={!token || loading}
            data-testid="ih-load"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-700 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>} Load
          </button>
          <button
            onClick={() => load(true)}
            disabled={!token || probing}
            data-testid="ih-probe"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-60"
          >
            {probing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Activity className="w-3.5 h-3.5"/>} Probe live
          </button>
        </div>

        {data && (
          <>
            <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3">
                <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">Live</div>
                <div className="font-display text-2xl font-extrabold text-emerald-800 tabular-nums" data-testid="ih-green-count">{data.summary?.green || 0}</div>
              </div>
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3">
                <div className="text-[10px] uppercase tracking-wide font-bold text-amber-700">Partial</div>
                <div className="font-display text-2xl font-extrabold text-amber-800 tabular-nums" data-testid="ih-amber-count">{data.summary?.amber || 0}</div>
              </div>
              <div className="rounded-2xl bg-rose-50 border border-rose-200 p-3">
                <div className="text-[10px] uppercase tracking-wide font-bold text-rose-700">Missing</div>
                <div className="font-display text-2xl font-extrabold text-rose-800 tabular-nums" data-testid="ih-red-count">{data.summary?.red || 0}</div>
              </div>
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm" data-testid="ih-table">
                <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Integration</th>
                    <th className="px-4 py-3 text-left">Purpose</th>
                    <th className="px-4 py-3 text-left">Surface</th>
                    {data.probed && <th className="px-4 py-3 text-left">Probe</th>}
                    <th className="px-4 py-3 text-left">Missing keys</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.surface || []).map((row) => {
                    const probe = data.probes?.[row.name];
                    const nice = NICE[row.name] || { label: row.name, purpose: "" };
                    return (
                      <tr key={row.name} className="border-t border-slate-100" data-testid={`ih-row-${row.name}`}>
                        <td className="px-4 py-3 font-bold text-slate-900">{nice.label}</td>
                        <td className="px-4 py-3 text-slate-600">{nice.purpose}</td>
                        <td className="px-4 py-3"><StatusPill status={row.status}/></td>
                        {data.probed && (
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <StatusPill status={probe?.status || "red"}/>
                              {probe?.error && <span className="text-[10px] text-rose-600 truncate max-w-[16ch]" title={probe.error}>{probe.error}</span>}
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-3 text-[11px]">
                          {(row.missing || []).length === 0
                            ? <span className="text-slate-400">—</span>
                            : <code className="text-rose-700">{(row.missing || []).join(", ")}</code>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
